// App surface: Auth (sign in / sign up), Project Dashboard (+ modal).
// Ported from the design source (screens-app.jsx) and bound to the live
// services: Cognito auth, the projects API, and publication state.
import { useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react';

import { useAuth } from '../auth/AuthProvider';
import { GeneratedCover } from '../components/GeneratedCover';
import { ImageSlot } from '../components/ImageSlot';
import { Arrow, Glyph, Logo, SearchIcon } from '../components/ui-shared';
import { VerifyWidget, useVerifyGate } from '../components/VerifyWidget';
import { SIGNUP_OPEN } from '../config/studioStatus';
import { Link, navigate } from '../design/router';
import { ThemeToggle } from '../design/theme';
import type { StudioProjectMeta, StudioProjectTemplate } from '../../engine';
import { createProject, duplicateProject, listProjects } from '../persistence/projectRepository';
import { loginWithEmail, loginWithGoogle, markSignupIntent, registerWithEmail } from '../services/auth';
import { SITE_KEY_AUTH } from '../services/captcha';
import { getPublication, type Publication } from '../services/publish';
import { toast } from '../design/toast';

export function AppStage({ children, fill, height = 900 }: { children: ReactNode; fill?: boolean; height?: number }) {
  return (
    <div
      className="ss-screen"
      style={{
        height: fill ? '100vh' : height,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

/** 'Edited 2 hours ago' style relative time, matching the design's tone. */
export function timeAgo(epochMs: number): string {
  const s = Math.max(0, (Date.now() - epochMs) / 1000);
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} minutes ago`;
  if (s < 7200) return '1 hour ago';
  if (s < 86400) return `${Math.round(s / 3600)} hours ago`;
  if (s < 172800) return 'yesterday';
  if (s < 604800) return `${Math.round(s / 86400)} days ago`;
  if (s < 1209600) return 'last week';
  return `${Math.round(s / 604800)} weeks ago`;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
  return (first + last).toUpperCase();
}

/* ── Auth ─────────────────────────────────────────────────────────────── */
function GoogleMark() {
  return (
    <svg width="15" height="15" viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 009 18z"
        fill="#34A853"
      />
      <path d="M3.97 10.72a5.41 5.41 0 010-3.44V4.95H.96a9 9 0 000 8.1l3.01-2.33z" fill="#FBBC05" />
      <path
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 00.96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function AuthTopBar() {
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 64,
        padding: '0 28px',
        flexShrink: 0,
      }}
    >
      <Logo />
      <Link to="/" className="mk-nav-link" style={{ fontSize: 13 }}>
        Back to site
      </Link>
    </div>
  );
}

export function ScreenAuth({ mode = 'signin', fill }: { mode?: 'signin' | 'signup'; fill?: boolean }) {
  const isUp = mode === 'signup';
  const [studioName, setStudioName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Soulstack Verify: one fresh token per attempt (see services/auth.ts for why
  // this gate is advisory). Google below is not gated, the provider vets it.
  const verify = useVerifyGate(SITE_KEY_AUTH);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!verify.token) {
      setError('Wait for the verification check to finish, then try again.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      if (isUp) {
        const { needsConfirmation } = await registerWithEmail(
          email,
          password,
          studioName || (email.split('@')[0] ?? ''),
          verify.token,
        );
        if (needsConfirmation) navigate('/verify', { state: { email, password } });
        else navigate('/app/welcome');
      } else {
        await loginWithEmail(email, password, verify.token);
        navigate('/app');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
      // The token is spent whether or not the attempt worked.
      verify.renew();
    }
  }

  async function google() {
    setError(null);
    try {
      if (isUp) markSignupIntent();
      await loginWithGoogle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in is not available.');
    }
  }

  return (
    <AppStage fill={fill}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'radial-gradient(ellipse 60% 45% at 50% 0%, var(--ss-glow), transparent 70%)',
        }}
      />
      <AuthTopBar />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <div style={{ width: 400, display: 'flex', flexDirection: 'column', gap: 28 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
            <Link to="/">
              <Glyph size={44} />
            </Link>
            <div>
              <h1 className="ss-display" style={{ fontSize: 28 }}>
                {isUp ? 'Create your studio' : 'Welcome back'}
              </h1>
              <p style={{ color: 'var(--ss-text-3)', fontSize: 14, marginTop: 8 }}>
                {isUp ? 'Free to start. Nothing to install.' : 'Sign in to keep building.'}
              </p>
            </div>
          </div>
          <form
            className="ss-card"
            style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 16, background: 'var(--ss-panel-strong)' }}
            onSubmit={submit}
          >
            <button type="button" className="ss-btn" style={{ background: '#fff', color: '#1a1f27', width: '100%' }} onClick={google}>
              <GoogleMark /> Continue with Google
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ss-text-3)', fontSize: 12 }}>
              <hr className="ss-hairline" style={{ flex: 1 }} />
              <span>or</span>
              <hr className="ss-hairline" style={{ flex: 1 }} />
            </div>
            {isUp && (
              <div className="ss-field">
                <span className="ss-field-label">Studio name</span>
                <input
                  className="ss-input"
                  placeholder="e.g. Night Lotus Games"
                  value={studioName}
                  onChange={(e) => setStudioName(e.target.value)}
                />
              </div>
            )}
            <div className="ss-field">
              <span className="ss-field-label">Email</span>
              <input
                className="ss-input"
                type="email"
                placeholder="you@studio.dev"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="ss-field">
              <span className="ss-field-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Password</span>
                {!isUp && (
                  <Link to="/reset" style={{ color: 'var(--ss-text-3)' }}>
                    Forgot?
                  </Link>
                )}
              </span>
              <input
                className="ss-input"
                type="password"
                placeholder="••••••••••"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <VerifyWidget gate={verify} />
            {error && (
              <p style={{ color: 'var(--ss-danger)', fontSize: 13, lineHeight: 1.5 }} role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              className="ss-btn ss-btn--primary"
              style={{ width: '100%', marginTop: 4 }}
              disabled={busy || !verify.token}
            >
              {busy ? 'One moment…' : isUp ? 'Create account' : 'Sign in'}
            </button>
          </form>
          <p style={{ textAlign: 'center', fontSize: 13.5, color: 'var(--ss-text-3)' }}>
            {isUp ? (
              <>
                {'Already have an account? '}
                <Link to="/signin" style={{ color: 'var(--ss-text)', fontWeight: 600 }}>
                  Sign in
                </Link>
              </>
            ) : SIGNUP_OPEN ? (
              <>
                {'New to Soulstack? '}
                <Link to="/signup" style={{ color: 'var(--ss-text)', fontWeight: 600 }}>
                  Create an account
                </Link>
              </>
            ) : (
              'New to Soulstack? Public sign-ups aren’t open yet - Studio is in active development.'
            )}
          </p>
        </div>
      </div>
    </AppStage>
  );
}

/**
 * Coming-soon screen shown at /signup while public sign-ups are closed (see
 * config/studioStatus). Keeps the door open for the team: existing accounts can
 * still sign in from here.
 */
export function ScreenComingSoon({ fill }: { fill?: boolean }) {
  return (
    <AppStage fill={fill}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'radial-gradient(ellipse 60% 45% at 50% 0%, var(--ss-glow), transparent 70%)',
        }}
      />
      <AuthTopBar />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <div
          style={{
            width: 440,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 20,
            textAlign: 'center',
          }}
        >
          <Link to="/">
            <Glyph size={48} />
          </Link>
          <span
            className="ss-eyebrow"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 12px',
              borderRadius: 999,
              background: 'var(--ss-accent-soft)',
              border: '1px solid var(--ss-accent-border)',
              color: 'var(--ss-accent)',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ss-accent)' }} />
            In development
          </span>
          <h1 className="ss-display" style={{ fontSize: 30 }}>
            Sign-ups are opening soon
          </h1>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 15, lineHeight: 1.6, maxWidth: 380 }}>
            Soulstack Studio is in active development. Public accounts aren&rsquo;t open yet - explore the
            platform in the meantime, and check back soon.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <Link className="ss-btn ss-btn--primary" to="/features">
              Explore the platform
            </Link>
            <Link className="ss-btn ss-btn--ghost" to="/signin">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </AppStage>
  );
}

/* ── App chrome ───────────────────────────────────────────────────────── */
export function AppTopNav() {
  const { user, signOut } = useAuth();
  const [menu, setMenu] = useState(false);
  const initials = initialsOf(user?.displayName || user?.email || '?');

  async function handleSignOut() {
    setMenu(false);
    await signOut();
    navigate('/');
  }

  return (
    <div className="app-topnav">
      <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
        <Logo to="/app" />
        <div className="app-topnav-links" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Link to="/app" className="mk-nav-link" style={{ color: 'var(--ss-text)', background: 'var(--ss-hover)' }}>
            Projects
          </Link>
          <Link to="/showcase" className="mk-nav-link">
            Showcase
          </Link>
          <Link to="/docs" className="mk-nav-link">
            Docs
          </Link>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative' }}>
        <span
          className="app-topnav-badge"
          style={{
            fontFamily: 'var(--ss-sans)',
            fontWeight: 500,
            fontSize: 11,
            letterSpacing: '0.1em',
            color: 'var(--ss-text-3)',
            whiteSpace: 'nowrap',
          }}
        >
          EARLY ACCESS
        </span>
        <ThemeToggle />
        <button
          onClick={() => setMenu(!menu)}
          aria-label="Account menu"
          aria-haspopup="true"
          aria-expanded={menu}
          style={{ border: 'none', background: 'transparent', padding: 0 }}
        >
          <div className="app-avatar">{initials}</div>
        </button>
        {menu && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 90 }} onClick={() => setMenu(false)}>
            <div
              style={{
                position: 'absolute',
                top: 58,
                right: 24,
                width: 220,
                background: 'var(--ss-surface)',
                border: '1px solid var(--ss-border)',
                borderRadius: 14,
                boxShadow: '0 24px 60px rgba(17,20,24,0.22)',
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: '10px 12px 12px', borderBottom: '1px solid var(--ss-border-soft)', marginBottom: 6 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{user?.displayName || 'Creator'}</div>
                <div style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>{user?.email}</div>
              </div>
              {/* Mirrors app-topnav-links: on a narrow viewport that row is
                  hidden (soulstack.css), so Showcase/Docs stay reachable here
                  instead of only via Projects (the Logo). */}
              <Link to="/showcase" className="pj-rail-link app-topnav-menu-mirror" onClick={() => setMenu(false)}>
                Showcase
              </Link>
              <Link to="/docs" className="pj-rail-link app-topnav-menu-mirror" onClick={() => setMenu(false)}>
                Docs
              </Link>
              <Link to="/app/account" className="pj-rail-link" onClick={() => setMenu(false)}>
                Account settings
              </Link>
              <Link to="/app/account#billing" className="pj-rail-link" onClick={() => setMenu(false)}>
                Plan & billing
              </Link>
              <button
                className="pj-rail-link"
                onClick={handleSignOut}
                style={{ color: 'var(--ss-text-3)', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}
              >
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── New project modal ────────────────────────────────────────────────── */
export const TEMPLATE_CHOICES: { name: string; template: StudioProjectTemplate; blurb: string }[] = [
  { name: 'Blank', template: 'blank', blurb: 'An empty world with the core asset pack and neutral biped.' },
  { name: 'Gallery', template: 'gallery', blurb: 'A furnished 3D room - shelving, a table and chairs, columns - to rearrange and build outward from.' },
  { name: 'Village', template: 'village', blurb: 'A top-down street of houses, a pond and trees to build outward from.' },
  { name: 'Platformer', template: 'platformer', blurb: 'A side-view level with a hazard to clear, checkpoint and bonus platforms, and a goal to build outward from.' },
];

/** Village and Platformer are both tilemaps and only render under a 2D projection - null means the creator's own 3D/2D choice stands. */
export function templateForcedDimension(template: StudioProjectTemplate): '2D' | null {
  return template === 'village' || template === 'platformer' ? '2D' : null;
}

export function NewProjectModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const templates = [
    ...TEMPLATE_CHOICES,
    { name: 'Import project', template: 'blank' as StudioProjectTemplate, blurb: 'Restore from an exported .sstk archive.' },
  ];
  const [active, setActive] = useState('Blank');
  const [dim, setDim] = useState('3D');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiveFile, setArchiveFile] = useState<File | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const importSelected = active === 'Import project';
  const selectedTemplate = templates.find((t) => t.name === active)?.template ?? 'blank';
  // The Village and Platformer starters are both tilemaps; either only
  // renders under a 2D projection (GameRuntime falls back to 3D for
  // anything else), so picking one locks the dimension choice the same way
  // Import already does.
  const forcedDim = templateForcedDimension(selectedTemplate);
  const dimLocked = importSelected || forcedDim !== null;
  const effectiveDim = forcedDim ?? dim;

  // Matches EdConfirmDialog's own Escape handling (editor-panels.tsx) - the
  // one dialog pattern in the app this one didn't yet share.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onClose]);

  async function create() {
    if (!user) return;
    setError(null);
    setBusy(true);
    try {
      if (importSelected) {
        if (!archiveFile) {
          setBusy(false);
          setError('Choose a .sstk file to import.');
          return;
        }
        const { parseArchive, importProjectArchive } = await import('../persistence/sstkArchive');
        const archive = parseArchive(await archiveFile.text());
        const projectId = await importProjectArchive(archive, user.id, name);
        navigate(`/app/editor/${projectId}/edit`);
        return;
      }
      const meta = await createProject({
        ownerUid: user.id,
        name: name.trim() || 'Untitled world',
        template: selectedTemplate,
        projection: effectiveDim === '2D' ? '2D' : '3D',
      });
      try {
        localStorage.setItem('ss-projection', effectiveDim);
      } catch {
        // best effort - the persisted project config is the source of truth
      }
      navigate(`/app/editor/${meta.id}/edit`);
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Could not create the project. Try again.');
    }
  }

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(4,4,5,0.66)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
      }}
      onClick={onClose}
    >
      <div
        className="ss-card"
        role="dialog"
        aria-modal="true"
        aria-label="New project"
        style={{
          width: 640,
          padding: 32,
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
          background: 'var(--ss-surface)',
          borderColor: 'var(--ss-border)',
          boxShadow: '0 30px 80px rgba(17,20,24,0.24)',
          borderRadius: 20,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h2 className="ss-display" style={{ fontSize: 22 }}>
            New project
          </h2>
          <p style={{ fontSize: 13.5, color: 'var(--ss-text-3)' }}>Pick a starting point - you can change everything later.</p>
        </div>
        <div className="ss-field">
          <span className="ss-field-label">Project name</span>
          <input
            className="ss-input"
            placeholder="Untitled world"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="ss-field">
          <span className="ss-field-label">Game dimension</span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, opacity: dimLocked ? 0.5 : 1 }}>
            {(
              [
                ['3D', 'Full 3D worlds, first or third person.'],
                ['2D', 'Top down or side view, sprites and tilemaps.'],
              ] as const
            ).map(([d, hint]) => (
              <button
                key={d}
                className="ss-card"
                disabled={dimLocked}
                onClick={() => setDim(d)}
                style={{
                  textAlign: 'left',
                  padding: '13px 15px',
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  cursor: dimLocked ? 'not-allowed' : 'pointer',
                  border: '1px solid ' + (effectiveDim === d && !dimLocked ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
                  background: effectiveDim === d && !dimLocked ? 'var(--ss-accent-soft)' : 'transparent',
                  color: 'var(--ss-text)',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600, color: effectiveDim === d && !dimLocked ? 'var(--ss-accent)' : 'var(--ss-text)' }}>{d}</span>
                <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.45 }}>{hint}</span>
              </button>
            ))}
          </div>
          <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>
            {importSelected
              ? "The imported archive's own dimension is used - this choice is ignored for an import."
              : forcedDim
                ? `${selectedTemplate === 'platformer' ? 'Platformer' : 'Village'} is a 2D starter, so this is fixed to 2D.`
                : 'One studio either way. The viewport and tools adapt; change it any time in Game Setup.'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          {templates.map((t) => (
            <button
              key={t.name}
              onClick={() => setActive(t.name)}
              className="ss-card"
              style={{
                textAlign: 'left',
                padding: '16px 16px 18px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                background: t.name === active ? 'var(--ss-accent-soft)' : 'var(--ss-hover)',
                borderColor: t.name === active ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)',
                borderRadius: 12,
                color: 'var(--ss-text)',
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</span>
              <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.45 }}>{t.blurb}</span>
            </button>
          ))}
        </div>
        {importSelected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              ref={importInputRef}
              type="file"
              accept=".sstk,application/json"
              style={{ display: 'none' }}
              onChange={(e) => setArchiveFile(e.target.files?.[0] ?? null)}
            />
            <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => importInputRef.current?.click()} style={{ alignSelf: 'flex-start' }}>
              {archiveFile ? archiveFile.name : 'Choose .sstk file'}
            </button>
            <span style={{ fontSize: 12, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
              Restores the map, every editor document and settings. Uploaded files (models, audio, images) are not included; re-upload them after import.
            </span>
          </div>
        )}
        {error && (
          <p style={{ color: 'var(--ss-danger)', fontSize: 13 }} role="alert">
            {error}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 2 }}>
          <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={onClose}>
            Cancel
          </button>
          <button className="ss-btn ss-btn--primary ss-btn--sm" onClick={create} disabled={busy || (importSelected && !archiveFile)}>
            {busy ? (importSelected ? 'Importing…' : 'Creating…') : importSelected ? 'Import project' : 'Create project'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Dashboard ────────────────────────────────────────────────────────── */
interface DashboardProject {
  id: string;
  name: string;
  meta: string;
  updatedAt: number;
  live?: boolean;
  coverUrl: string | null;
}

function DuplicateIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M5.5 5.5h8v8h-8zM10.5 5.5v-3h-8v8h3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Matches ProjectCard's own shape, so a loading dashboard reads as
 * "fetching your projects" rather than looking identical to "you have
 * none yet" - both used to be just the dashed New-project tile. */
function ProjectCardSkeleton() {
  return (
    <div className="ss-card" style={{ padding: 9, display: 'flex', flexDirection: 'column' }} aria-hidden="true">
      <div className="ss-skeleton" style={{ width: '100%', height: 150, borderRadius: 10 }} />
      <div style={{ padding: '14px 9px 8px', display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div className="ss-skeleton" style={{ width: '65%', height: 14.5 }} />
        <div className="ss-skeleton" style={{ width: '40%', height: 12 }} />
      </div>
    </div>
  );
}

function ProjectCard({ p, onDuplicate }: { p: DashboardProject; onDuplicate: (id: string) => Promise<void> }) {
  const [duplicating, setDuplicating] = useState(false);

  async function duplicate(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (duplicating) return;
    setDuplicating(true);
    try {
      await onDuplicate(p.id);
    } finally {
      setDuplicating(false);
    }
  }

  return (
    <Link to={`/app/project/${p.id}/overview`} className="ss-card" style={{ padding: 9, display: 'flex', flexDirection: 'column' }}>
      <div className="thumb-wrap">
        {p.coverUrl ? (
          <ImageSlot shape="rect" src={p.coverUrl} alt={p.name + ' cover'} style={{ width: '100%', height: 150 }} />
        ) : (
          <GeneratedCover name={p.name} />
        )}
        {p.live && (
          <span className="thumb-badge" style={{ top: 10, left: 10, color: 'var(--ss-success)', border: '1px solid color-mix(in srgb, var(--ss-success) 25%, transparent)' }}>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--ss-success)' }} />
            LIVE
          </span>
        )}
      </div>
      <div style={{ padding: '14px 9px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ fontSize: 14.5, fontWeight: 600 }}>{p.name}</span>
          <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>{p.meta}</span>
        </div>
        <button
          type="button"
          className="row-action"
          onClick={duplicate}
          disabled={duplicating}
          title="Duplicate project"
          aria-label={`Duplicate ${p.name}`}
          style={{ border: 'none', background: 'transparent', color: 'var(--ss-text-3)', cursor: duplicating ? 'default' : 'pointer', padding: 4, display: 'flex' }}
        >
          <DuplicateIcon />
        </button>
        <span className="row-action" style={{ color: 'var(--ss-text-3)' }}>
          <Arrow />
        </span>
      </div>
    </Link>
  );
}

export function ScreenDashboard({ fill }: { fill?: boolean }) {
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<DashboardProject[]>([]);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('Recently edited');
  const searchRef = useRef<HTMLInputElement>(null);

  async function handleDuplicate(id: string) {
    try {
      const meta = await duplicateProject(id);
      setProjects((prev) => [
        { id: meta.id, name: meta.name, meta: 'Edited ' + timeAgo(meta.updatedAt), updatedAt: meta.updatedAt, live: false, coverUrl: meta.coverUrl ?? null },
        ...prev,
      ]);
      toast(`Duplicated as "${meta.name}"`, 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not duplicate the project.', 'error');
    }
  }

  // The search field advertises a ⌘K badge; bind the shortcut it promises.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const metas: StudioProjectMeta[] = await listProjects();
        // Per-project resilience: one project's publish-status check failing
        // (now a real, distinguishable error rather than a silent 404-like
        // null) must not take down the whole list - fall back to null just
        // for that project instead of rejecting the Promise.all.
        const pubs: (Publication | null)[] = await Promise.all(
          metas.map((m) =>
            getPublication(m.id).catch((err: unknown) => {
              console.error('[dashboard] failed to load publish status for', m.id, err);
              return null;
            }),
          ),
        );
        if (cancelled) return;
        setProjects(
          metas.map((m, i) => ({
            id: m.id,
            name: m.name,
            meta: 'Edited ' + timeAgo(m.updatedAt),
            updatedAt: m.updatedAt,
            live: pubs[i]?.visibility === 'public' || pubs[i]?.visibility === 'unlisted',
            coverUrl: m.coverUrl ?? null,
          })),
        );
      } catch {
        if (!cancelled) setError('Could not load your projects. Check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = useMemo(() => {
    const filtered = projects.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()));
    if (sort === 'Name') return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    return [...filtered].sort((a, b) => b.updatedAt - a.updatedAt);
  }, [projects, query, sort]);

  const liveCount = projects.filter((p) => p.live).length;
  const subtitle =
    projects.length === 0
      ? 'No projects yet'
      : `${projects.length} project${projects.length === 1 ? '' : 's'}${liveCount ? ` · ${liveCount} live` : ''}`;

  return (
    <AppStage fill={fill}>
      <AppTopNav />
      <div style={{ flex: 1, overflow: 'auto', padding: '40px 48px' }}>
        <div className="dash-page" style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div className="dash-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, gap: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h1 className="ss-display" style={{ fontSize: 30 }}>
                Projects
              </h1>
              <span style={{ fontSize: 13.5, color: 'var(--ss-text-3)' }}>{loading ? 'Loading…' : subtitle}</span>
            </div>
            <div className="dash-head-controls" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="ss-search dash-search">
                <SearchIcon />
                <input
                  ref={searchRef}
                  className="ss-input"
                  placeholder="Search projects"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ padding: '10px 46px 10px 35px' }}
                />
                <span className="ss-kbd">⌘K</span>
              </div>
              <select
                className="ss-input dash-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                style={{ padding: '10px 12px', fontSize: 13, appearance: 'none' }}
              >
                <option>Recently edited</option>
                <option>Name</option>
              </select>
              <button className="ss-btn ss-btn--primary" onClick={() => setModal(true)}>
                + New project
              </button>
            </div>
          </div>
          {error && (
            <p style={{ color: 'var(--ss-danger)', fontSize: 13.5, marginBottom: 18 }} role="alert">
              {error}
            </p>
          )}
          <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {loading ? (
              <>
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
              </>
            ) : (
              <>
                {shown.map((p) => (
                  <ProjectCard key={p.id} p={p} onDuplicate={handleDuplicate} />
                ))}
                <button
                  className="ss-card"
                  onClick={() => setModal(true)}
                  style={{
                    minHeight: 230,
                    border: '1px dashed var(--ss-border)',
                    background: 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    color: 'var(--ss-text-3)',
                    fontSize: 13.5,
                    fontWeight: 500,
                    borderRadius: 'var(--ss-r-lg)',
                  }}
                >
                  <span style={{ fontSize: 26, fontWeight: 300, lineHeight: 1 }}>+</span>
                  New project
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {modal && <NewProjectModal onClose={() => setModal(false)} />}
    </AppStage>
  );
}
